<template>
    <td
        role="cell"
        :style="containerStyle"
        :class="containerClass"
        @click="setFocus"
        :data-p-frozen-column="frozen"
        :data-p-editable-column="editableCell"
        ref="cellRef"
    >
        <div class="md-editable-grid-cell-wrapper" @dblclick="onCellDblClick">
            <!-- Editing mode -->
            <div
                v-if="isEditing"
                v-click-outside:[datePickerName]="closeDropdown"
                class="editable-date-cell-content-wrapper"
            >
                <div
                    :class="{
                        'md-editable-grid-cell-content-invalid': invalid,
                        'md-editable-grid-cell-editing': !invalid
                    }"
                    class="md-editable-grid-cell-content"
                    contenteditable="true"
                    ref="inputRef"
                    @keydown="handleKeydown"
                    @blur="finishEdit"
                >
                    {{ displayValue }}
                </div>

                <div class="editable-date-cell-icon-container" @mousedown.prevent="toggleDropdown">
                    <i>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 1024 1024"
                            width="1em"
                            height="1em"
                            fill="#19BEE8"
                        >
                            <path
                                d="M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z"
                            ></path>
                        </svg>
                    </i>
                </div>
            </div>

            <!-- Display mode -->
            <div v-else class="editable-date-cell-display-wrapper">
                <span
                    class="md-editable-grid-cell-display"
                    :class="{
                        'md-editable-grid-cell-non-editable': !editableCell,
                        'span-placeholder': placeholderVisible
                    }"
                    @dblclick="requestEditHandler"
                    @keydown="handleDisplayKeydown"
                    tabindex="0"
                    ref="displayRef"
                >
                    {{ displayValue || placeholder }}
                </span>

                <!-- DateTime picker icon -->
                <div
                    v-if="editableCell"
                    class="editable-date-cell-display-icon"
                    @mousedown.prevent="requestEditHandler"
                >
                    <i>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 1024 1024"
                            width="1em"
                            height="1em"
                            fill="#19BEE8"
                        >
                            <path
                                d="M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z"
                            ></path>
                        </svg>
                    </i>
                </div>
            </div>

            <date-picker
                v-show="isEditing && showDropdown"
                v-model="dateTimeValue"
                :type="'datetime'"
                :inline="true"
                :format="dateTimeFormat"
                :lang="lang"
                :id="datePickerName"
                input-class="form-control"
                @change="datePickerChanged"
            >
                <template v-slot:header="{ emit }">
                    <button class="mx-btn mx-btn-text mx-btn-local" @click="selectToday(emit)">
                        {{ $t('Shared.Now') }}
                    </button>
                </template>
            </date-picker>
        </div>
    </td>
</template>

<script>
import Utils from '@/common/utils.js';
import EditableCellBase from './EditableCellBase.vue';
import ShowMessage from '@/components/messages/ShowMessage.js';

export default {
    name: 'EditableDateTimeCell',
    inject: ['$parentInstance'],
    extends: EditableCellBase,
    mixins: [ShowMessage],
    props: {
        value: {
            type: [Date, String],
            default: null
        },
        // '24h' or '12h'
        timeFormat: {
            type: String,
            default: '12h',
            validator: v => ['24h', '12h'].includes(v)
        },
        // 'mm' or 'mm:ss'
        precision: {
            type: String,
            default: 'mm',
            validator: v => ['mm', 'mm:ss'].includes(v)
        },
        invalidFormatAction: {
            type: String,
            default: 'initial',
            validator: value => ['null', 'initial'].includes(value)
        },
        invalidFormatWarning: {
            type: String,
            default: 'cell',
            validator: value => ['none', 'cell', 'message', 'both'].includes(value)
        }
    },
    data() {
        return {
            dropdownElement: null,
            showDropdown: false,
            dateFormat: Utils.getFormatComponentDatePicker('date'),
            displayValue: null,
            formattedValue: null,
            dateTimeValue: null,
            dropdownRect: null
        };
    },
    computed: {
        dateTimeFormat() {
            let result =
                this.timeFormat === '12h' ? `${this.dateFormat} hh:mm` : `${this.dateFormat} HH:mm`;

            if (this.precision === 'mm:ss') {
                result += ':ss';
            }

            if (this.timeFormat === '12h') {
                result += ' A';
            }

            return result;
        },
        placeholder() {
            if (this.placeholderVisible) {
                let result =
                    this.timeFormat === '12h'
                        ? `${this.dateFormat} hh:mm`
                        : `${this.dateFormat} HH:mm`;

                if (this.precision === 'mm:ss') {
                    result += ':ss';
                }

                if (this.timeFormat === '12h') {
                    result += ' A';
                }

                return result;
            }

            return '';
        },

        placeholderVisible() {
            return this.editableCell && this.dateTimeValue === null;
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                let cleanVal = newValue;
                const parsed = cleanVal ? this.parse(cleanVal) : null;
                this.displayValue = this.formattedValue = this.formatDate(parsed);
                this.dateTimeValue = parsed;
            }
        },
        showDropdown(val) {
            val ? this.renderDropdown() : this.removeDropdown();
        },
        precision() {
            let cleanVal = this.value;
            const parsed = cleanVal ? this.parse(cleanVal) : null;
            this.displayValue = this.formattedValue = this.formatDate(parsed);
        },
        timeFormat() {
            let cleanVal = this.value;
            const parsed = cleanVal ? this.parse(cleanVal) : null;
            this.displayValue = this.formattedValue = this.formatDate(parsed);
        }
    },
    methods: {
        startEdit(char) {
            if (!this.editableCell) {
                this.displayContentFocus();
                return;
            }

            this.isEditing = true;
            if (char !== undefined) {
                this.$nextTick(() => {
                    const el = this.$refs.inputRef;
                    if (el) {
                        el.innerText = char;
                        el.focus();
                        this.moveCaretToEnd(el);
                    }
                });
            } else {
                this.inputContentFocus();
            }
        },
        finishEdit(event) {
            if (!this.showDropdown && this.isEditing) {
                this.isEditing = false;
                const val = event.target.innerText.trim();
                const changed = val !== String(this.formattedValue).trim();

                if (changed) {
                    const parsed = this.parse(val);
                    const valISO = this.emitInternalDate(parsed);
                    const valid = this.validateCell(valISO);

                    if (!valid) {
                        this.showMessage(
                            this.$tc('Msg.Error'),
                            this.invalidMessage(this.name),
                            'errorAutoHide'
                        );
                    }
                }
            }
        },
        closeDropdown() {
            this.showDropdown = false;
            this.finishEdit({ target: this.$refs.inputRef });
        },
        removeDropdown() {
            /*if (this.dropdownElement?.parentNode) {
                this.dropdownElement.parentNode.removeChild(this.dropdownElement);
                this.dropdownElement = null;
            }*/
        },
        toggleDropdown() {
            if (!this.editableCell) {
                return;
            }

            this.showDropdown = !this.showDropdown;

            if (this.showDropdown) {
                const val = this.$refs.inputRef.innerText.trim();
                this.dateTimeValue = this.parse(val);
                this.$refs.inputRef.focus();
            }
        },
        renderDropdown() {
            this.$nextTick(() => {
                if (!this.$refs.cellRef) {
                    return;
                }

                const datePicker = document.getElementById(this.datePickerName);
                if (!datePicker) {
                    return;
                }

                var _this$_popupRect = this.dropdownRect,
                    width = _this$_popupRect.width,
                    height = _this$_popupRect.height;

                const _getRelativePosition = this.getRelativePosition(
                        this.$refs.cellRef,
                        width,
                        height,
                        true
                    ),
                    left = _getRelativePosition.left,
                    top = _getRelativePosition.top;

                Object.assign(datePicker.style, {
                    position: 'absolute',
                    top: top,
                    left: left,
                    zIndex: '999999'
                    //display: 'block'
                });

                /*const rowNumber = 5;
                const rect = this.$refs.cellRef.getBoundingClientRect();
                const listHeight = Math.min(rowNumber * 40, 200);
                const spaceBelow = window.innerHeight - rect.bottom;
                const positionTop = spaceBelow < listHeight && rect.top > listHeight;

                const top = positionTop
                    ? rect.top + window.scrollY - listHeight
                    : rect.bottom + window.scrollY;

                if (!this.dropdownElement) {
                    this.dropdownElement = document.createElement('div');
                    document.body.appendChild(this.dropdownElement);
                }

                Object.assign(this.dropdownElement.style, {
                    position: 'absolute',
                    top: `${top}px`,
                    left: `${rect.left + window.scrollX}px`,
                    width: `${rect.width}px`,
                    zIndex: '999999',
                    display: 'block'
                });

                //this.dropdownElement.innerHTML = '<span>OK</span>';*/
            });
        },
        requestEdit() {
            EditableCellBase.methods.requestEdit.call(this);

            this.$nextTick(() => {
                if (this.isEditing && !this.showDropdown) {
                    this.toggleDropdown();
                }
            });
        },
        parse(val) {
            if (!val) {
                return null;
            }

            if (val instanceof Date) {
                return val;
            }

            if (typeof val === 'string') {
                if (val.includes('T')) {
                    const [datePart, timePart] = val.split('T');
                    const [year, month, day] = datePart.split('-').map(Number);

                    let hour = 0;
                    let minute = 0;
                    let second = 0;

                    if (timePart) {
                        const [hh, mm, ss = '00'] = timePart.split(':');
                        hour = parseInt(hh, 10) || 0;
                        minute = parseInt(mm, 10) || 0;
                        second = parseInt(ss, 10) || 0;
                    }

                    return new Date(year, month - 1, day, hour, minute, second);
                }

                // yyyy-MM-dd
                if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
                    const [year, month, day] = val.split('-').map(Number);
                    return new Date(year, month - 1, day);
                }
            }

            // manual
            let [datePart, timePart, period] = val.split(' ');
            const parts = datePart.split('/');
            if (parts.length !== 3 || datePart.length !== 10) {
                return this.dateTimeValue;
            }

            let hour = 0;
            let minute = 0;
            let second = 0;

            if (timePart) {
                const [hh, mm, ss = '00'] = timePart.split(':');
                hour = parseInt(hh, 10) || 0;
                minute = parseInt(mm, 10) || 0;
                second = parseInt(ss, 10) || 0;

                if (period && period.toUpperCase() === 'PM' && hour < 12) {
                    hour += 12;
                    period = null;
                }
            }

            let day, month, year;

            if (this.dateFormat && this.dateFormat.startsWith('DD')) {
                day = +parts[0];
                month = +parts[1];
                year = +parts[2];
            } else {
                month = +parts[0];
                day = +parts[1];
                year = +parts[2];
            }

            return this.isValidDate(year, month, day) &&
                this.isValidTime(hour, minute, second, period)
                ? new Date(year, month - 1, day, hour, minute, second)
                : this.dateTimeValue;
        },
        formatDate(date) {
            if (!date) {
                return null;
            }

            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();

            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';

            if (this.timeFormat === '12h') {
                hours = hours % 12;

                if (hours === 0) {
                    hours = 12;
                }
            }

            const hh = String(hours).padStart(2, '0');
            let datePart = `${month}/${day}/${year}`;

            if (this.dateFormat && this.dateFormat.startsWith('DD')) {
                datePart = `${day}/${month}/${year}`;
            }

            if (this.precision === 'mm') {
                if (this.timeFormat === '12h') {
                    return `${datePart} ${hh}:${minutes} ${ampm}`;
                }

                return `${datePart} ${hh}:${minutes}`;
            }

            if (this.timeFormat === '12h') {
                return `${datePart} ${hh}:${minutes}:${seconds} ${ampm}`;
            }

            return `${datePart} ${hh}:${minutes}:${seconds}`;
        },
        isValidDate(year, month, day) {
            if (month < 1 || month > 12) {
                return false;
            }

            const maxDays = [
                31,
                (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28,
                31,
                30,
                31,
                30,
                31,
                31,
                30,
                31,
                30,
                31
            ];

            return day >= 1 && day <= maxDays[month - 1];
        },
        isValidTime(hh, min, ss, period) {
            if (min < 0 || min > 59) {
                return false;
            }

            if (ss < 0 || ss > 59) {
                return false;
            }

            if (period) {
                if (period.toUpperCase() === 'PM') {
                    return hh > 0 && hh <= 12;
                } else if (period.toUpperCase() === 'AM') {
                    return hh >= 0 && hh <= 11;
                } else {
                    return false;
                }
            } else if (hh < 0 || hh > 23) {
                return false;
            }

            return true;
        },
        emitInternalDate(val) {
            if (!val) {
                if (this.isAppendSection) {
                    this.$emit('input', null);
                } else {
                    this.$parentInstance.cellEditComplete(this.createEventData(null));
                }

                return null;
            }

            const pad = n => String(n).padStart(2, '0');
            const yyyy = val.getFullYear();
            const mm = pad(val.getMonth() + 1);
            const dd = pad(val.getDate());
            const hh = pad(val.getHours());
            const mi = pad(val.getMinutes());
            const ss = this.precision === 'mm:ss' ? pad(val.getSeconds()) : '00';

            let result = `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;

            if (this.isAppendSection) {
                this.$emit('input', result);
            } else {
                this.$parentInstance.cellEditComplete(this.createEventData(result));
            }

            return result;
        },
        handleKeydown(event) {
            const allowedChars = ['a', 'A', 'm', 'M', 'p', 'P', ':', '/', ' '];

            if (/^[0-9]$/.test(event.key) || allowedChars.includes(event.key)) {
                return;
            }

            const allowedKeys = [
                'Backspace',
                'Tab',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Delete',
                'Escape',
                'Enter'
            ];

            if (allowedKeys.includes(event.key)) {
                switch (event.key) {
                    case 'Enter':
                    case 'Tab':
                    case 'Escape':
                        this.showDropdown = false;
                        break;
                    case 'ArrowUp':
                        if (this.caretAtStart()) {
                            this.showDropdown = false;
                        }
                        break;
                    case 'ArrowDown':
                        if (this.caretAtEnd()) {
                            this.showDropdown = false;
                        }
                        break;
                    case 'ArrowLeft':
                        if (this.caretAtStart() && !this.isAllSelected()) {
                            this.showDropdown = false;
                        }
                        break;
                    case 'ArrowRight':
                        if (this.caretAtEnd() && !this.isAllSelected()) {
                            this.showDropdown = false;
                        }
                        break;
                }

                EditableCellBase.methods.handleKeydown.call(this, event);
                return;
            }

            event.preventDefault();
        },
        datePickerChanged() {
            const formattedValue = this.formatDate(this.dateTimeValue);
            //this.showDropdown = false;

            const el = this.$refs.inputRef;
            if (el) {
                el.innerText = formattedValue;
                el.focus();
                this.moveCaretToEnd(el);
            }
        },
        selectToday() {
            this.dateTimeValue = new Date();
            this.datePickerChanged();
            this.showDropdown = false;
        }
    },

    mounted() {
        const datePicker = document.getElementById(this.datePickerName);

        if (datePicker) {
            this.dropdownRect = this.getElementSize(datePicker);
            document.body.appendChild(datePicker);
        }
    },

    directives: {
        'click-outside': {
            bind(el, binding, vnode) {
                el.clickOutsideEvent = e => {
                    const datePicker = document.getElementById(binding.arg);
                    const insideDatePicker = datePicker && datePicker.contains(e.target);

                    if (!(el === e.target || el.contains(e.target) || insideDatePicker)) {
                        vnode.context[binding.expression]();
                    }
                };
                document.body.addEventListener('mousedown', el.clickOutsideEvent);
            },
            unbind(el) {
                document.body.removeEventListener('mousedown', el.clickOutsideEvent);
            }
        }
    }
    /*
  

  props: {
    name: { type: String, required: true },
    value: String, // ISO datetime: YYYY-MM-DDTHH:mm:ss
    editable: { type: Boolean, default: true },
    timeFormat: {
      type: String,
      default: '24h',
      validator: v => ['24h', '12h'].includes(v)
    },
    precision: {
      type: String,
      default: 'mm',
      validator: v => ['mm','mm:ss'].includes(v)
    }
  },

  data() {
    const shortFormat = Utils.getShortDateFormat(); // "DD/MM/YYYY" o "MM/DD/YYYY"
    const dateFormatOrder = shortFormat.startsWith('DD') ? 'DMY' : 'MDY';

    return {
      isEditing: false,
      invalid: false,
      placeholder: shortFormat + ' HH:mm',
      format: shortFormat + ' HH:mm',
      Value: '',
      rawDateDigits: '',
      rawTimeDigits: '',
      period: '',
      dateFormatOrder 
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          this.rawDateDigits = '';
          this.rawTimeDigits = '';
          this.period = '';
          this.Value = '';
          return;
        }
        const [datePart, timePart] = newVal.split('T');
        const formattedDate = Utils.formatterDateToString(datePart);
        this.rawDateDigits = formattedDate.replace(/\D/g, '');

        if (timePart) {
          const [h, m, s='00'] = timePart.split(':');
          let hours = parseInt(h,10);
          this.period = '';

          if(this.timeFormat==='12h'){
            this.period = hours>=12?'PM':'AM';
            hours = hours%12 || 12;
          }
          this.rawTimeDigits = hours.toString().padStart(2,'0') + m +
            (this.precision==='mm:ss'?s:'');
        } else {
          this.rawTimeDigits='';
        }
        
        this.Value = this.buildMaskedValue();
      }
    }
  },

  methods:{
    enterEditMode(){
      if(!this.editable) {
        return;
      }
      
      this.isEditing = true;
      this.$nextTick(()=>this.$refs.textInput.focus());
    },

    onBlur(){
      this.$nextTick(()=>{
        if(document.activeElement!==this.$refs.textInput){
          this.isEditing = false;
          this.emitDateTime();
        }
      });
    },

    buildMaskedValue(){
      let result='';
      const d=this.rawDateDigits;

      if(d.length>0) {
        result+=d.substring(0,2);
      }

      if(d.length>2) {
        result+='/'+d.substring(2,4);
      }

      if(d.length>4) {
        result+='/'+d.substring(4,8);
      }

      if(this.rawTimeDigits.length>0) {
        result+=' ';
      }

      const t=this.rawTimeDigits;

      if(t.length>0) {
        result+=t.substring(0,2);
      }

      if(t.length>2) {
        result+=':'+t.substring(2,4);
      }

      if(this.precision==='mm:ss' && t.length>4) {
        result+=':'+t.substring(4,6);
      }

      if(this.timeFormat==='12h' && t.length>=2) {
          result+=this.period?' '+this.period:'';
      }

      return result;
    },

    onInput(e){
      const input = e.target;
      let val = input.value;
      const cursorPos = input.selectionStart;

      const [datePart,...rest] = val.split(' ');
      const timePart = rest.join(' ');

      let dateDigits = datePart.replace(/\D/g,'').substring(0,8);
      this.rawDateDigits = dateDigits;

      let timeDigits = timePart.replace(/\D/g,'');
      const maxTimeLen = this.precision==='mm:ss'?6:4;
      timeDigits = timeDigits.substring(0,maxTimeLen);
      this.rawTimeDigits = timeDigits;

      if(this.timeFormat==='12h'){
        const ampmMatch = timePart.match(/(A|P)M/i);

        if(ampmMatch) {
          this.period = ampmMatch[0].toUpperCase();
        }
      }

      this.Value = this.buildMaskedValue();

      this.$nextTick(()=>{
        let newPos = cursorPos;

        if(cursorPos===2||cursorPos===5) {
          newPos++;
        }

        if(cursorPos===10) {
          newPos++;
        }

        if(cursorPos===13||cursorPos===16) {
          newPos++;
        }

        input.setSelectionRange(newPos,newPos);
      });

      this.emitIfComplete();
    },

    onKeyDown(e){
      const input = e.target;
      const pos = input.selectionStart;
      const allowedKeys=[
        'Backspace',
        'Tab',
        'ArrowLeft',
        'ArrowRight',
        'Delete'
      ];

      if(allowedKeys.includes(e.key)) {
        return;
      }

      if(e.key==='/' && (pos===2||pos===5)){
        e.preventDefault(); 
        input.setSelectionRange(pos+1,pos+1); 
        return;
      }

      if(e.key===':' && (pos===13||pos===16)){
        e.preventDefault(); 
        input.setSelectionRange(pos+1,pos+1); 
        return;
      }

      if(e.key===' ' && pos===10){
        e.preventDefault(); 
        input.setSelectionRange(pos+1,pos+1); 
        return;
      }

      if(this.timeFormat==='12h'){
        if(e.key.toUpperCase()==='A'){ 
          this.period='AM';
          e.preventDefault(); 
          return; 
        }

        if(e.key.toUpperCase()==='P'){ 
          this.period='PM';
          e.preventDefault(); 
          return; 
        }
      }

      if(!/^\d$/.test(e.key)) {
        e.preventDefault();
      }

      if(e.key==='Enter'||e.key==='Tab'){
        e.preventDefault();
        this.emitDateTime();
        this.$emit('next-cell');
      }
    },

    onPaste(e){
      const clipboard=(e.clipboardData||window.clipboardData).getData('text');
      const digits=clipboard.replace(/\D/g,'');
      const maxDateLen=8;
      const maxTimeLen=this.precision==='mm:ss'?6:4;

      this.rawDateDigits=digits.substring(0,maxDateLen);
      this.rawTimeDigits=digits.substring(maxDateLen,maxDateLen+maxTimeLen);

      const ampmMatch=clipboard.match(/(A|P)M/i);

      if(this.timeFormat==='12h' && ampmMatch) {
        this.period=ampmMatch[0].toUpperCase();
      }

      this.Value=this.buildMaskedValue();
      this.emitIfComplete();
    },

    emitIfComplete(){
      const dateComplete=this.rawDateDigits.length===8;
      const timeComplete=this.rawTimeDigits.length=== (this.precision==='mm:ss'?6:4);

      if(dateComplete && timeComplete) {
        this.emitDateTime();
      }
    },

    emitDateTime(){
      if(!this.rawDateDigits||!this.rawTimeDigits){
        this.$emit('input','');
        this.emitChanged('');
        return;
      }

      const day=this.rawDateDigits.substring(0,2);
      const month=this.rawDateDigits.substring(2,4);
      const year=this.rawDateDigits.substring(4,8);
      const isoDate=this.dateOrder==='DMY'?`${year}-${month}-${day}`:
        `${year}-${day}-${month}`;

      let h=parseInt(this.rawTimeDigits.substring(0,2),10);
      const m=this.rawTimeDigits.substring(2,4);
      const s=this.precision==='mm:ss'?this.rawTimeDigits.substring(4,6):'00';

      if(this.timeFormat==='12h'){
        if(this.period==='PM' && h<12) {
          h+=12;
        }

        if(this.period==='AM' && h===12) {
          h=0;
        }
      }
      const hh=h.toString().padStart(2,'0');
      const isoTime=`${hh}:${m}:${s}`;
      const isoDateTime=`${isoDate}T${isoTime}`;

      this.$emit('input',isoDateTime);
      this.emitChanged(isoDateTime);
    },

    emitChanged(value){
      EventBus.$emit('onChanged',{
        Changed:true,
        ComponentName:this.name,
        Value:value
      });
    }
  }*/
};
</script>

<style scoped>
.md-editable-grid-cell-display {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.editable-date-cell-content-wrapper {
    display: flex;
    align-items: stretch;
    width: 100%;
    height: 100%;
    position: relative;
}

.editable-date-cell-icon-container {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #19bee8;
    cursor: pointer;
    z-index: 11;
}
tbody.md-editable-grid-body-append .editable-date-cell-icon-container {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #19bee8;
    cursor: pointer;
    z-index: 11;
}

/* Display mode: flex-based so the icon stays within the cell scroll area */
.editable-date-cell-display-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    overflow: hidden;
}
.editable-date-cell-display-wrapper .md-editable-grid-cell-display {
    flex: 1;
    min-width: 0;
}
.editable-date-cell-display-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding-right: 6px;
    color: #19bee8;
    cursor: pointer;
}
tbody.md-editable-grid-body-append .editable-date-cell-display-icon {
    color: #19bee8;
}
.span-placeholder {
    color: lightgrey;
}
</style>
